// src/components/aols/aeo/OLSupdates/OLS2900/OLS2908.tsx
import React from "react";
import { Layout, Typography, Breadcrumb } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { HomeOutlined } from "@ant-design/icons";
import { InlineShareButtons } from "sharethis-reactjs";
import Footer from "../../../../dev/utils/Footer/Footer";
import { updates2900 } from "./OLS2900";

const { Title, Paragraph } = Typography;

const UpdateContainer = styled.div`
  background: #000000;
  min-height: 100vh;
  padding: 40px 20px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UpdateContent = styled.div`
  max-width: 800px;
  width: 100%;
  background: #1a1a1a;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const UpdateTitle = styled(Title)`
  color: #ffcc00 !important;
  margin-bottom: 20px;
`;

const UpdateParagraph = styled(Paragraph)`
  color: #fff !important;
  font-size: 16px;
  line-height: 1.8;
  margin-bottom: 20px;
`;

const UpdateLink = styled(Link)`
  color: #ffaa00 !important;
  text-decoration: none;
  font-size: 16px;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00 !important;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }
`;

const HighlightedText = styled.span`
  font-size: 48px;
  color: #ffcc00;
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const ShareThisWrapper = styled.div`
  margin-top: 20px;
  padding: 10px;
  background: #1a1a1a;
  border-radius: 8px;
  display: flex;
  justify-content: center;

  .st-btn {
    border: 1px solid #ffcc00 !important;
    border-radius: 4px !important;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }

    &:focus {
      outline: 2px solid #40c4ff;
      outline-offset: 2px;
    }
  }

  .st-total {
    color: #fff !important;
    font-size: 16px;
  }
`;

const CallToAction = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: #ffcc00;
  border-radius: 8px;
  display: inline-block;
`;

const CallToActionText = styled(Paragraph)`
  color: #0427f4 !important;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const OLS2908: React.FC = () => {
  const update = updates2900.find((u) => u.id === 2908);

  if (!update) {
    return null; // Render nothing if the update is not found
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "OLSLM: 2908.110.4582 - onelightsystem OLS",
    description: "Latest OLSLM update from onelightsystem OLS: Light Time measurement added to OLS Inventions, enabling users to view correct Light Time based on Sun rhythms.",
    url: "https://www.olsme.com/olsupdates/ols2900/2908",
    datePublished: "2025-04-10",
    author: {
      "@type": "Organization",
      name: "onelightsystem OLS",
      url: "https://www.olsme.com",
    },
    publisher: {
      "@type": "Organization",
      name: "onelightsystem OLS",
      url: "https://www.olsme.com",
      logo: "https://www.olsme.com/logo.jpg",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://www.olsme.com/olsupdates/ols2900/2908",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.olsme.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "OLS Updates",
          item: "https://www.olsme.com/olsupdates",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "OLS2900",
          item: "https://www.olsme.com/olsupdates/ols2900",
        },
        {
          "@type": "ListItem",
          position: 4,
          name: "OLSLM: 2908.110.4582",
          item: "https://www.olsme.com/olsupdates/ols2900/2908",
        },
      ],
    },
  };

  return (
    <Layout>
      <Helmet>
        <title>OLSLM: 2908.110.3405 - onelightsystem OLS</title>
        <meta
          name="description"
          content="Latest OLSLM update from onelightsystem OLS: Light Time measurement added to OLS Inventions, enabling users to view correct Light Time based on Sun rhythms."
        />
        <meta
          name="keywords"
          content="OLSLM update, onelightsystem OLS, Light Time measurement, Sun Light Meditation, OLS updates, Light Civilization, meditation education, OLS Inventions"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="onelightsystem OLS" />
        <link rel="canonical" href="https://www.olsme.com/olsupdates/ols2900/2908" />
        <meta property="og:title" content="OLSLM: 2908.110.4582 - onelightsystem OLS" />
        <meta
          property="og:description"
          content="Latest OLSLM update from onelightsystem OLS: Light Time measurement added to OLS Inventions, enabling users to view correct Light Time based on Sun rhythms."
        />
        <meta property="og:url" content="https://www.olsme.com/olsupdates/ols2900/2908" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="onelightsystem OLS Logo for OLSLM Update" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="onelightsystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:title" content="OLSLM: 2908.110.4582 - onelightsystem OLS" />
        <meta
          name="twitter:description"
          content="Latest OLSLM update from onelightsystem OLS: Light Time measurement added to OLS Inventions, enabling users to view correct Light Time based on Sun rhythms."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="onelightsystem OLS Logo for OLSLM Update" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <BreadcrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/">
              <HomeOutlined /> Home
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/olsupdates">OLS Updates</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/olsupdates/ols2900">OLS2900</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>OLSLM: 2908.110.3405</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbWrapper>

      <UpdateContainer>
        <UpdateContent>
          <UpdateTitle level={1}>OLSLM: 2908.110.3405</UpdateTitle>
          <UpdateParagraph>
            We’re excited to announce a new update from the onelightsystem OLS web development team! As part of our commitment to advancing the Light Civilization, we’ve introduced a groundbreaking feature to the OLS Inventions section.
          </UpdateParagraph>
          <UpdateParagraph>
            <HighlightedText>Light Time Measurement</HighlightedText> is now available, allowing users to view the correct Light Time based on Sun rhythms. This innovative tool aligns human activity with natural light cycles, enhancing meditation efficacy, biological clocks, and overall well-being.
          </UpdateParagraph>
          <UpdateParagraph>
            Visit the <UpdateLink to="/OLSinventions">OLS Inventions</UpdateLink> page to explore this new feature and see how Light Time can transform your daily rhythm. We’re also thrilled to share that the <UpdateLink to="/OLScalendarTime">OLS Calendar Time</UpdateLink> page now includes a detailed visualization of Light Time, making it easier than ever to integrate into your life.
          </UpdateParagraph>
          <UpdateParagraph>
            Additionally, today we’ve rolled out several enhancements to improve your experience on olsme.com:
          </UpdateParagraph>
          <UpdateParagraph>
            - <strong>Global Light Time Toggle:</strong> The Light Time mini toggle is now visible across all pages, placed in the top-right corner alongside the OLS Calendar toggle. It dynamically updates to show the current Light Hour (LH) or dark hour (dh) with corrected styling: black background with a red border for dh, and yellow background with dark blue text for LH. Check it out on any page, such as the <UpdateLink to="/OLScalendarTime">OLS Calendar Time</UpdateLink> page.
          </UpdateParagraph>
          <UpdateParagraph>
            - <strong>Estate Marketplace Navigation:</strong> In the Login Monitor section, estate values (e.g., 104,000 $) are now clickable, redirecting to specific property detail pages like <UpdateLink to="/estate/mount-shasta-2.5-acres">Mount Shasta 2.5 Acres</UpdateLink>. This makes it easier to explore properties directly from the monitor.
          </UpdateParagraph>
          <UpdateParagraph>
            - <strong>Improved Property Detail Styling:</strong> On the <UpdateLink to="/estate">Estate Marketplace</UpdateLink> property detail pages, we’ve enhanced readability by changing the description text and active breadcrumb items to white, ensuring better contrast against the dark background while keeping prices in yellow for emphasis.
          </UpdateParagraph>
          <UpdateParagraph>
            These updates reflect our ongoing mission to provide tools that foster human consciousness evolution through Sun Light Meditation. Stay tuned for more exciting developments from onelightsystem OLS!
          </UpdateParagraph>
          <CallToAction>
            <CallToActionText>
              Explore Light Time Today—Align with the Sun!
            </CallToActionText>
          </CallToAction>

          {/* ShareThis Plugin */}
          <ShareThisWrapper>
            <InlineShareButtons
              config={{
                alignment: "center",
                color: "social",
                enabled: true,
                font_size: 16,
                labels: "cta",
                language: "en",
                networks: [
                  "whatsapp",
                  "linkedin",
                  "messenger",
                  "facebook",
                  "twitter",
                  "blogger",
                  "pinterest",
                  "sms",
                ],
                padding: 12,
                radius: 4,
                show_total: true,
                size: 20,
                min_count: 10,
                url: "https://www.olsme.com/olsupdates/ols2900/2908",
                image: "https://www.olsme.com/logo.jpg",
                description: "Latest OLSLM update from onelightsystem OLS: Light Time measurement added to OLS Inventions, enabling users to view correct Light Time based on Sun rhythms.",
                title: "OLSLM: 2908.110.3405 - onelightsystem OLS",
                message: "Check out this latest OLSLM update from onelightsystem OLS!",
                subject: "OLSLM Update: 2908.110.3405",
                username: "onelightsystem",
              }}
            />
          </ShareThisWrapper>
        </UpdateContent>
      </UpdateContainer>

      <Footer />
    </Layout>
  );
};

export default OLS2908;